import { useState, useEffect, useCallback, useRef } from 'react';
import messageQueueService, { MessageStatus } from '../services/messageQueueService';

/**
 * React hook exposing the per-room message queue state for a given roomId.
 *
 * Provides:
 *  - `enqueue(sendFn, meta)` – add a message to the room's send queue.
 *  - `retryUnsent()` – retry all unsent messages.
 *  - `dismiss(itemId)` – remove an unsent message.
 *  - `isBusy` – true while the queue is actively sending or retrying.
 *  - `hasUnsent` – true when there are messages the user can manually resend.
 *  - `queueSnapshot` – array of item status objects for the UI.
 *
 * @param {string|null} roomId
 */
const useMessageQueue = (roomId) => {
  const [isBusy, setIsBusy] = useState(false);
  const [hasUnsent, setHasUnsent] = useState(false);
  const [queueSnapshot, setQueueSnapshot] = useState([]);
  const roomIdRef = useRef(roomId);
  roomIdRef.current = roomId;

  // Sync state from the service whenever the queue changes
  const refreshState = useCallback((targetRoomId) => {
    if (targetRoomId !== roomIdRef.current) return;
    setIsBusy(messageQueueService.isBusy(targetRoomId));
    setHasUnsent(messageQueueService.hasUnsent(targetRoomId));
    setQueueSnapshot(messageQueueService.getQueueSnapshot(targetRoomId));
  }, []);

  useEffect(() => {
    if (!roomId) return;

    const handler = (event) => {
      refreshState(event.detail.roomId);
    };

    // Clean up all queues on session invalidation (logout)
    const sessionHandler = () => {
      messageQueueService.destroyAll();
    };

    messageQueueService.addEventListener('queueChange', handler);
    window.addEventListener('matrix:session-invalidated', sessionHandler);
    // Initial sync
    refreshState(roomId);

    return () => {
      messageQueueService.removeEventListener('queueChange', handler);
      window.removeEventListener('matrix:session-invalidated', sessionHandler);
    };
  }, [roomId, refreshState]);

  const enqueue = useCallback((sendFn, meta = {}) => {
    if (!roomId) return Promise.reject(new Error('No roomId'));
    return messageQueueService.enqueue(roomId, sendFn, meta);
  }, [roomId]);

  const retryUnsent = useCallback(() => {
    if (roomId) messageQueueService.retryUnsent(roomId);
  }, [roomId]);

  const dismiss = useCallback((itemId) => {
    if (roomId) messageQueueService.dismiss(roomId, itemId);
  }, [roomId]);

  return {
    enqueue,
    retryUnsent,
    dismiss,
    isBusy,
    hasUnsent,
    queueSnapshot,
    MessageStatus,
  };
};

export default useMessageQueue;
